import React from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import { applyMiddleware, createStore, combineReducers, compose } from 'redux';
import { Provider } from 'react-redux';
import { persistStore, persistReducer } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import localforage from 'localforage';
import reducers from './store/reducers';
import thunk from 'redux-thunk';
import logger from 'redux-logger';
import get from 'lodash/get';
import LoginView from './containers/LoginView';
import AuthView from './containers/AuthView';
import Dashboard from './containers/Dashboard';
import LogoutView from './containers/LogoutView';
import AuthClientView from './containers/AuthClientView';
import ClientEnrollment from './containers/ClientEnrollment';
import SubmitPolicyView from './containers/SubmitPolicyView';
import { logException, sanitizeSentryStatePayload } from './services/utils';
import * as analytics from './services/analytics';
import sureAxios from './services/sureAxios';
import * as Sentry from '@sentry/browser';
import { logout } from './store/actions';
import './index.css';

/**
 * Polyfills
 */
import 'react-app-polyfill/ie11';

const ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

/**
 * Initialize third-party
 */

if (process.env.REACT_APP_SEGMENT_WRITE_KEY) {
  analytics.load(process.env.REACT_APP_SEGMENT_WRITE_KEY);
}

if (!window.Cypress && process.env.REACT_APP_SENTRY_DSN) {
  const apiRegExp = new RegExp(process.env.REACT_APP_SURE_API_BASE_URL);
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: ENV,
    maxBreadcrumbs: 20,
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.category === 'xhr') {
        return apiRegExp.test(get(breadcrumb, 'data.url')) ? breadcrumb : null;
      } else {
        return breadcrumb;
      }
    },
    integrations: [
      new Sentry.Integrations.Breadcrumbs({ console: ENV === 'production' }),
    ],
  });
}
window.stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * Initialize store
 */
const storeMiddleware = [thunk];

if (process.env.REACT_APP_ENV !== 'production') {
  storeMiddleware.push(logger);
}

if (process.env.REACT_APP_SENTRY_DSN) {
  storeMiddleware.push((store) => (next) => (action) => {
    Sentry.addBreadcrumb({
      message: action.type,
      category: 'redux_action',
      level: 'info',
    });
    Sentry.configureScope((scope) => {
      scope.setExtra('state', sanitizeSentryStatePayload(store.getState()));
    });
    next(action);
  });
}

const middlewareEnhancer = applyMiddleware(...storeMiddleware);
const enhancers = [middlewareEnhancer];
const composeEnhancers =
  process.env.REACT_APP_ENV !== 'production' &&
  window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;
const composedEnhancers = composeEnhancers(...enhancers);

const persistConfig = {
  key: 'sureBroker',
  storage: localforage,
  blacklist: ['searchResults'],
};
const persistedReducer = persistReducer(
  persistConfig,
  combineReducers(reducers)
);

const store = createStore(persistedReducer, composedEnhancers);
const persistor = persistStore(store);

/**
 * Configure axios
 */
sureAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    logException(error);
    if (get(error, 'response.data.error.code') === 'invalid_login_creds') {
      store.dispatch(logout());
    }
    return Promise.reject(error);
  }
);

const SureAuth = () => {
  const { authToken, user } = store.getState();
  analytics.identify(user);
  sureAxios.defaults.headers['Authorization'] = `Bearer ${authToken}`;
  return null;
};

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <SureAuth />
      <Router>
        <Switch>
          {/* Broker side */}
          <Route exact path="/login" component={LoginView} />
          <Route exact path="/auth" component={AuthView} />
          <Route exact path="/logout" component={LogoutView} />
          <Route path="/dashboard" component={Dashboard} />

          {/* Client side */}
          <Route exact path="/enroll" component={AuthClientView} />
          <Route exact path="/submit-policy" component={SubmitPolicyView} />
          <Route path="/products" component={ClientEnrollment} />
          <Route path="/buy" component={ClientEnrollment} />

          <Redirect from="/" to="/dashboard" />
        </Switch>
      </Router>
    </PersistGate>
  </Provider>,
  document.getElementById('root')
);
// registerServiceWorker();
